import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {Dispatch, SetStateAction} from 'react';
import {z as r} from 'zod';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {fetchAndDeserialize, serializeToURLParameters} from '../../util/api-adapter';
import {ApiError, HttpError} from '../../util/error';
import {objectToUrlParameters} from '../../util/url';

import {companiesImportUrl} from './companies-import-const';

const companiesImportRunSchema = r.object({
    taskId: r.string(),
});

type CompaniesImportRunType = r.infer<typeof companiesImportRunSchema>;

function getCompaniesImportRunUrl(id: number, periodicUpdate: boolean): string {
    const url = `${companiesImportUrl}/import_url/${id}/run_import/`;

    if (periodicUpdate) {
        return `${url}?${objectToUrlParameters(serializeToURLParameters({periodicUpdate: true}))}`;
    }

    return url;
}

function fetchCompaniesImportRun(id: number, periodicUpdate: boolean): Promise<CompaniesImportRunType> {
    return fetchAndDeserialize(getCompaniesImportRunUrl(id, periodicUpdate), companiesImportRunSchema);
}

export function useCompaniesImportRunMutation(
    id: number,
    setIsImportCancelled: Dispatch<SetStateAction<boolean>>
): UseMutationResult<CompaniesImportRunType, unknown, boolean> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation((periodicUpdate: boolean) => fetchCompaniesImportRun(id, periodicUpdate), {
        onError: (error: unknown) => {
            if ((error instanceof ApiError || error instanceof HttpError) && error.statusCode === 403) {
                setIsImportCancelled(true);
                snackbar.error(getLocalizedString('ERROR__ACCESS_DENIED_BANNER__SUB_TITLE'));
            }
        },
    });
}
